import { z } from "zod";
import { baseURL } from "../../../../../config/Endpoints.js";
import { getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { OffboardingDetailSchema, OffboardingListSchema, OffboardingQuestionSchema, OffboardingRequestSchema } from "../schemas/index.ts";
import type { OffboardingApi } from "./types.ts";

export const SQE_OFFBOARDING_URL = `${baseURL}/api/sqe/offboarding`;

export function unwrapSqeOffboardingResponse(value: unknown): unknown {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const response = value as Record<string, unknown>;
    if (typeof response.statusCode === "number" && "data" in response) return response.data;
  }
  return value;
}

// SQE omits regular-only flags. Normalize solely at the SQE boundary.
export function normalizeSqeOffboarding(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const row = value as Record<string, unknown>;
  const normalized: Record<string, unknown> = { ...row, retentionRequested: row.retentionRequested ?? false };
  for (const key of ["cpoApproved", "cooApproved", "exitInterviewCompleted", "withdrawalRequested", "itCompleted", "hrCompleted", "payrollCompleted", "adminCompleted", "rmCompleted", "payrollHold"]) {
    normalized[key] = row[key] ?? false;
  }
  return normalized;
}

export function parseSqeOffboarding(value: unknown) {
  return OffboardingRequestSchema.parse(normalizeSqeOffboarding(value));
}

function rows(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  const payload = value as Record<string, unknown>;
  if (Array.isArray(payload.content)) return payload.content;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

export const sqeOffboardingApi: OffboardingApi = {
  async getList(status, signal) {
    const url = status ? `${SQE_OFFBOARDING_URL}/list/${status}` : `${SQE_OFFBOARDING_URL}/list`;
    const { data } = await getApi<unknown>(url, accessToken(), "json", signal);
    return OffboardingListSchema.parse(rows(unwrapSqeOffboardingResponse(data)).map(normalizeSqeOffboarding));
  },
  async getDetail(offboardingId, signal) {
    const { data } = await getApi<unknown>(`${SQE_OFFBOARDING_URL}/${offboardingId}`, accessToken(), "json", signal);
    const payload = unwrapSqeOffboardingResponse(data);
    if (!payload || typeof payload !== "object") throw new Error("Invalid SQE offboarding detail.");
    const detail = payload as Record<string, unknown>;
    const offboarding = detail.offboarding && typeof detail.offboarding === "object"
      ? { ...(detail.offboarding as Record<string, unknown>), employeeName: (detail.offboarding as Record<string, unknown>).employeeName ?? detail.employeeName }
      : detail.offboarding;
    const normalized: Record<string, unknown> = { ...detail, offboarding: normalizeSqeOffboarding(offboarding) };
    for (const key of ["retentionAnswers", "resignationAnswers", "exitInterviewAnswers", "itAnswers", "hrAnswers", "payrollAnswers", "adminAnswers", "rmAnswers"]) {
      normalized[key] = detail[key] ?? null;
    }
    return OffboardingDetailSchema.parse(normalized);
  },
  async getQuestionnaire(offboardingId, section, signal) {
    const { data } = await getApi<unknown>(`${SQE_OFFBOARDING_URL}/questionnaire/${offboardingId}/${section}`, accessToken(), "json", signal);
    return z.array(OffboardingQuestionSchema).parse(unwrapSqeOffboardingResponse(data) ?? []);
  },
};
